import React from 'react';
import RiskProfile from './RiskProfile';
import CapitalPizza from './CapitalPizza';

// Modo SIMPLES da gestão de risco: o leigo opera por NÍVEL (cards 🛡️/⚖️/🚀, que pré-preenchem os 7
// knobs) + uma âncora visual (pizza de exposição + checklist de proteções) + 2 sliders-chave com
// microcopy que EXPLICA o efeito. Edita o MESMO estado que o modo avançado (só uma projeção visual dos
// campos) → trocar de modo não perde valor. Parte do backlog project_backlog_simplified_ui_mode.

const num = v => (v === '' || v == null ? 0 : Number(v));

// Slider-chave: range + valor proeminente + microcopy do efeito + marca do recomendado. 0 = desligado.
function KeySlider({ label, value, onChange, min, max, recommend, help }) {
    const off = value <= min;
    return (
        <div className="mb-3">
            <div className="d-flex justify-content-between align-items-baseline">
                <label className="form-label fw-bold mb-0">{label}</label>
                <span className={`fw-bold ${off ? 'text-muted' : 'text-primary'}`} style={{ fontSize: 18 }}>
                    {off ? 'desligado' : `${value}%`}
                </span>
            </div>
            <input type="range" className="form-range" min={min} max={max} step="1"
                value={value} onChange={e => onChange(parseInt(e.target.value))} />
            <small className="text-muted d-block">
                {help}{recommend ? <> Recomendado: <strong>{recommend}%</strong>.</> : null}
            </small>
        </div>
    );
}

// Item do checklist de proteções: bolinha verde (ligada) / cinza (desligada). Clicável quando é um
// liga/desliga simples (BTC, pump/dump) — edição direta no checklist sem ir pro avançado.
function ProtItem({ on, label, onToggle }) {
    const dot = <span className="d-inline-block rounded-circle me-2"
        style={{ width: 9, height: 9, background: on ? '#28a745' : '#adb5bd', verticalAlign: 'middle' }} />;
    const body = <span style={{ fontSize: 13, color: on ? '#212529' : '#6c757d' }}>{dot}{label}</span>;
    if (!onToggle) return <div className="mb-2">{body}</div>;
    return (
        <button type="button" className="btn btn-link p-0 mb-2 text-decoration-none d-block text-start"
            onClick={onToggle} title="Clique para ligar/desligar">{body}</button>
    );
}

function RiskSimpleView({ settings, onChange, onShowAdvanced }) {
    const set = (field, value) => onChange(field, value);
    const exposure = num(settings.maxExposurePercent);
    const daily = num(settings.dailyDrawdownLimit);
    const btcOn = settings.btcRegimeFilter === true;
    const pumpOn = num(settings.pumpDumpFilterPercent) > 0;

    return (
        <div>
            {/* 1) NÍVEL — pré-preenche os 7 knobs de risco */}
            <RiskProfile settings={settings} onChange={onChange} />

            {/* 2) ÂNCORA VISUAL — pizza de exposição + proteções ligadas */}
            <div className="row align-items-center g-3 mt-1 mb-2">
                <div className="col-md-6">
                    <CapitalPizza exposurePercent={settings.maxExposurePercent} />
                </div>
                <div className="col-md-6">
                    <div className="fw-bold mb-2">Proteções</div>
                    <ProtItem on={exposure > 0 && exposure < 100}
                        label={`Teto de exposição${exposure > 0 && exposure < 100 ? ` (${exposure}%)` : ' (desligado)'}`} />
                    <ProtItem on={daily > 0}
                        label={`Freio de perda diária${daily > 0 ? ` (${daily}%)` : ' (desligado)'}`} />
                    <ProtItem on={btcOn} label="Filtro de baixa do BTC"
                        onToggle={() => set('btcRegimeFilter', !btcOn)} />
                    <ProtItem on={pumpOn} label="Filtro de pump & dump"
                        onToggle={() => set('pumpDumpFilterPercent', pumpOn ? '' : 15)} />
                    <small className="text-muted d-block mt-1">Bolinha verde = proteção ativa. Clique nas duas últimas para ligar/desligar.</small>
                </div>
            </div>

            {/* 3) SLIDERS-CHAVE — os dois levers de maior impacto, com microcopy do efeito */}
            <div className="mt-2">
                <KeySlider label="Exposição máxima da carteira" value={exposure} min={0} max={99} recommend={70}
                    onChange={v => set('maxExposurePercent', v === 0 ? '' : v)}
                    help="Trava novas compras quando o valor das posições passa desta % do patrimônio — protege numa queda (cripto cai em bloco)." />
                <KeySlider label="Freio de perda diária" value={daily} min={0} max={50} recommend={5}
                    onChange={v => set('dailyDrawdownLimit', v === 0 ? '' : v)}
                    help="Pausa TODAS as automações se a perda do dia passar disso." />
            </div>

            {/* ATIVOS AO MESMO TEMPO (Top-N) — o param que mais confunde o leigo; agora visível no Simples.
                aiTopN é o knob de runtime (effectiveTopN = aiTopN ?? perfil). Cada ativo precisa de capital. */}
            <div className="mb-3">
                <label className="form-label fw-bold mb-1">Ativos ao mesmo tempo</label>
                <div className="btn-group w-100" role="group" aria-label="Ativos simultâneos">
                    {[1, 2, 3, 4, 5].map(n => {
                        const cur = num(settings.aiTopN) || 1;
                        return (
                            <button key={n} type="button"
                                className={`btn btn-sm ${cur === n ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => set('aiTopN', n)}>{n}</button>
                        );
                    })}
                </div>
                <small className="text-muted d-block mt-1">
                    Quantos ativos o bot opera em paralelo. Cada um precisa de capital livre pra um grid
                    (~120 USDT na Binance) — mais ativos = mais diversificação, porém exige mais saldo.
                </small>
            </div>

            <button type="button" className="btn btn-link btn-sm px-0" onClick={onShowAdvanced}>
                ▸ Mostrar parâmetros avançados
            </button>
        </div>
    );
}

export default RiskSimpleView;
